/**
 * `v1-bulk` strategy — the pre-routeOps production shape per intent kind.
 * `assign`/`move`/`resequence` → single `bulkMoveStops` call with each
 * batch's `updateMasterSequence` mapped from `shouldIncludeMaster(intent)`.
 * `unassign` → daily + (optional) master(futureStops:true), no FE-side
 * future-daily fanout. `change-date` → daily-only via `buildChangeDatePipeline`,
 * or atomic `bulkChangeDate` when `includeMaster:true`. `change-day-of-week`
 * → master-only via `buildChangeDayOfWeekPipeline`.
 */

import { assertNever, err } from 'types/entities/shared/result'
import { isDailyStop, isOnRequestStop } from 'types/modules/routing'

import { shouldIncludeMaster } from '../decisions'
import { buildBulkInsertBatch, buildBulkResequenceBatch } from '../payloads'
import { buildChangeDatePipeline } from '../pipelines/changeDate'
import { buildChangeDayOfWeekPipeline } from '../pipelines/changeDayOfWeek'
import { buildCompletePipeline } from '../pipelines/complete'
import { buildMasterPublishPipeline } from '../pipelines/masterPublish'
import { buildRestoreDailyVersionPipeline } from '../pipelines/restoreDailyVersion'
import { buildRestoreMasterVersionPipeline } from '../pipelines/restoreMasterVersion'
import { buildSyncDailyToMasterPipeline } from '../pipelines/syncDailyToMaster'
import { makeBulkChangeDateStep } from '../steps/bulkChangeDate'
import { makeBulkMutateStep } from '../steps/bulkMutate'
import { makePreflightStep } from '../steps/preflight'
import { makeUnassignDailyStep } from '../steps/unassignDaily'
import { makeVerifyStep } from '../steps/verify'

import type { RouteOpStrategy } from './index'
import type { BulkChangeDateBatch, BulkMoveBatch, BulkMoveStopsBody } from 'types/api/route/bulkRequests'
import type { WorkOrderId } from 'types/entities/shared/ids'
import type { Deps, OperationPlan, PipelineStep, RouteOpIntent, RouteOpsStrategy, Stop } from 'types/modules/routing'

const STRATEGY_ID: RouteOpsStrategy = 'v1-bulk'

const collectUnassignWorkOrderIds = (intent: Extract<RouteOpIntent, { kind: 'unassign' }>): ReadonlyArray<WorkOrderId> => {
  const workOrderIds: WorkOrderId[] = []
  const seen = new Set<string>()
  intent.stops.forEach(stop => {
    if ((isDailyStop(stop) || isOnRequestStop(stop)) && stop.workOrderId && !seen.has(stop.workOrderId)) {
      seen.add(stop.workOrderId)
      workOrderIds.push(stop.workOrderId)
    }
  })
  return workOrderIds
}

/**
 * Two shapes:
 *  - `includeMaster: true`  → single `bulkMutate` call with
 *      `destinationRouteId: null` + `updateMasterSequence: true`. The BE
 *      atomically unassigns daily, master (auto-filtered to recurring via
 *      `filterRecurringStops`), and the future-daily cascade. We pass ALL
 *      workOrderIds (daily + on-request) — the BE handles filtering.
 *  - `includeMaster: false` → daily-only via the v2 unassign endpoint, same
 *      as pre-routeOps.
 */
const unassignPlan = (intent: Extract<RouteOpIntent, { kind: 'unassign' }>, deps: Deps): OperationPlan => {
  const workOrderIds = collectUnassignWorkOrderIds(intent)
  const steps: PipelineStep[] = [makePreflightStep({ intent }, deps) as PipelineStep]

  if (workOrderIds.length === 0) {
    steps.push(makeVerifyStep({ intent, expectedUnassignedWorkOrderIds: [] }, deps) as PipelineStep)
    return { strategy: STRATEGY_ID, intent, steps }
  }

  if (shouldIncludeMaster(intent)) {
    // BE-atomic: bulkMoveStops with `destinationRouteId: null` does daily +
    // future + master in one transaction; on-request stops are safe to
    // include because `filterRecurringStops` drops them on the master side.
    const batch: BulkMoveBatch = {
      destinationRouteId: null,
      stops: [...workOrderIds],
      serviceDate: String(intent.serviceDate),
      targetStopId: null,
      updateMasterSequence: true,
    }
    const body: BulkMoveStopsBody = { moves: [batch] }
    steps.push(makeBulkMutateStep({ body }, deps) as PipelineStep)
  } else {
    steps.push(makeUnassignDailyStep({ routeId: intent.routeId, workOrderIds }, deps) as PipelineStep)
  }

  steps.push(
    makeVerifyStep(
      {
        intent,
        expectedUnassignedWorkOrderIds: workOrderIds.map(String),
      },
      deps
    ) as PipelineStep
  )

  return { strategy: STRATEGY_ID, intent, steps }
}

const collectStopWorkOrderIds = (intent: Extract<RouteOpIntent, { kind: 'assign' | 'move' | 'resequence' }>): ReadonlyArray<string> => {
  const ids: string[] = []
  const seen = new Set<string>()
  const source = intent.kind === 'resequence' ? intent.orderedStops : intent.stops
  source.forEach(stop => {
    if (isDailyStop(stop) || isOnRequestStop(stop)) {
      const id = String(stop.workOrderId)
      if (id && !seen.has(id)) {
        seen.add(id)
        ids.push(id)
      }
    }
  })
  return ids
}

// Shared by `assign` and `move`. When an ordered list is provided, walk it
// and emit one bulkMoveStops batch per contiguous run of new stops anchored
// to the preceding existing row (CS-2750, mirrors pre-routeOps useGetBEBatches).
// Falls back to a single batch when the caller passes an explicit targetStopId
// or omits the ordered list.
const buildSingleBatchInsertPlan = (intent: Extract<RouteOpIntent, { kind: 'assign' | 'move' }>, deps: Deps): OperationPlan => {
  const workOrderIds = collectStopWorkOrderIds(intent)
  if (workOrderIds.length === 0) {
    return {
      strategy: STRATEGY_ID,
      intent,
      steps: [makePreflightStep({ intent }, deps) as PipelineStep],
    }
  }
  const explicit = intent.kind === 'assign' ? intent.targetStopId : undefined
  const ordered = intent.kind === 'assign' ? intent.allOrderedStops : intent.destinationOrderedStops
  const destinationRouteId = String(intent.routeId)
  const serviceDate = String(intent.serviceDate)
  const updateMasterSequence = shouldIncludeMaster(intent)

  let body: BulkMoveStopsBody
  if (ordered && ordered.length > 0 && explicit == null) {
    // `buildBulkInsertBatch`'s predicate is `row.routeId !== destinationRouteId`;
    // omit `routeId` on moved-in rows so they satisfy it without a magic
    // sentinel string.
    const newIds = new Set(workOrderIds.map(String))
    const rows = ordered.map(stop => {
      const id = String(stop.workOrderId)
      return newIds.has(id) ? { id } : { id, routeId: destinationRouteId }
    })
    const batches = buildBulkInsertBatch({ rows, destinationRouteId, serviceDate, updateMasterSequence })
    if (batches.length === 0) {
      // No moved-in rows identified in `ordered` (dialog UI typically prevents
      // reaching here). Exit clean — preflight succeeds, no mutation, no
      // verify so the caller doesn't claim work that wasn't done.
      return {
        strategy: STRATEGY_ID,
        intent,
        steps: [makePreflightStep({ intent }, deps) as PipelineStep],
      }
    }
    body = {
      moves: batches.map(batch => ({
        destinationRouteId: batch.destinationRouteId,
        stops: [...batch.stops],
        serviceDate: batch.serviceDate,
        targetStopId: batch.targetStopId,
        updateMasterSequence: batch.updateMasterSequence,
      })),
    }
  } else {
    const targetStopId: string | null = explicit != null ? String(explicit) : null
    body = {
      moves: [{ destinationRouteId, stops: [...workOrderIds], serviceDate, targetStopId, updateMasterSequence }],
    }
  }
  return {
    strategy: STRATEGY_ID,
    intent,
    steps: [
      makePreflightStep({ intent }, deps) as PipelineStep,
      makeBulkMutateStep({ body }, deps) as PipelineStep,
      makeVerifyStep({ intent, expectedAssignedWorkOrderIds: workOrderIds }, deps) as PipelineStep,
    ],
  }
}

const assignBulkPlan = (intent: Extract<RouteOpIntent, { kind: 'assign' }>, deps: Deps): OperationPlan =>
  buildSingleBatchInsertPlan(intent, deps)

const moveBulkPlan = (intent: Extract<RouteOpIntent, { kind: 'move' }>, deps: Deps): OperationPlan =>
  buildSingleBatchInsertPlan(intent, deps)

const changeDateBulkPlan = (intent: Extract<RouteOpIntent, { kind: 'change-date' }>, deps: Deps): OperationPlan => {
  // Daily-only "Move between days" — BE positions stops on its own.
  if (!shouldIncludeMaster(intent)) {
    return { ...buildChangeDatePipeline(intent, deps), strategy: STRATEGY_ID }
  }
  // includeMaster:true — atomic daily date flip + master CSE day-of-week flip
  // via POST /api/v1/core/route/stops/change-date/bulk. Mirrors pre-routeOps
  // BulkPermanentRecurrenceChangeDialog (commit 81aac97c4).
  const workOrderIds: string[] = []
  const seenWo = new Set<string>()
  intent.stops.forEach(stop => {
    if ((isDailyStop(stop) || isOnRequestStop(stop)) && stop.workOrderId) {
      const id = String(stop.workOrderId)
      if (!seenWo.has(id)) {
        seenWo.add(id)
        workOrderIds.push(id)
      }
    }
  })
  if (workOrderIds.length === 0) {
    return {
      strategy: STRATEGY_ID,
      intent,
      steps: [makePreflightStep({ intent }, deps) as PipelineStep],
    }
  }
  const routeId = String(intent.routeId)
  const targetDate = String(intent.toServiceDate)
  // CS-2750: when the dialog passes the full target-day ordering, emit one
  // BulkChangeDateBatch per contiguous run of moved-in rows (mirrors
  // pre-routeOps `getBEBatches` walking `row.date !== toFEDate`). Reuses
  // buildBulkInsertBatch's BulkBatch output; field-renamed below.
  const singleBatch: BulkChangeDateBatch = { routeId, stops: workOrderIds, targetDate, targetStopId: null }
  let moves: ReadonlyArray<BulkChangeDateBatch>
  if (intent.allOrderedStops && intent.allOrderedStops.length > 0) {
    // Omit `routeId` on moved-in rows so `buildBulkInsertBatch`'s predicate
    // (`row.routeId !== destinationRouteId`) fires without a magic sentinel.
    const newIds = new Set(workOrderIds)
    const rows = intent.allOrderedStops.map(stop => {
      const id = String(stop.workOrderId)
      return newIds.has(id) ? { id } : { id, routeId }
    })
    const batches = buildBulkInsertBatch({
      rows,
      destinationRouteId: routeId,
      serviceDate: targetDate,
      updateMasterSequence: false,
    })
    if (batches.length === 0) {
      // No moved-in row identified in `allOrderedStops` (dialog UI typically
      // prevents reaching here). Exit clean — preflight only, no mutation.
      return {
        strategy: STRATEGY_ID,
        intent,
        steps: [makePreflightStep({ intent }, deps) as PipelineStep],
      }
    }
    moves = batches.map(batch => ({
      routeId: batch.destinationRouteId,
      stops: [...batch.stops],
      targetDate: batch.serviceDate,
      targetStopId: batch.targetStopId,
    }))
  } else {
    moves = [singleBatch]
  }
  return {
    strategy: STRATEGY_ID,
    intent,
    steps: [
      makePreflightStep({ intent }, deps) as PipelineStep,
      makeBulkChangeDateStep({ body: { moves: [...moves] } }, deps) as PipelineStep,
      makeVerifyStep({ intent, expectedAssignedWorkOrderIds: workOrderIds }, deps) as PipelineStep,
    ],
  }
}

const resequenceBulkPlan = (intent: Extract<RouteOpIntent, { kind: 'resequence' }>, deps: Deps): OperationPlan => {
  const workOrderIds = collectStopWorkOrderIds(intent)
  if (workOrderIds.length === 0) {
    return {
      strategy: STRATEGY_ID,
      intent,
      steps: [makePreflightStep({ intent }, deps) as PipelineStep],
    }
  }
  // When the dialog tags rows with `isChanged`, emit one bulkMoveStops batch
  // per contiguous run of changed rows (CS-2750, mirrors pre-routeOps
  // useGetBEBatchesResequenceVariant). Otherwise fall back to a single
  // full-reorder batch — what Bulk Route Optimization wants.
  const destinationRouteId = String(intent.routeId)
  const serviceDate = String(intent.serviceDate)
  const updateMasterSequence = shouldIncludeMaster(intent)
  const fullReorderBody: BulkMoveStopsBody = {
    moves: [{ destinationRouteId, stops: [...workOrderIds], serviceDate, targetStopId: null, updateMasterSequence }],
  }

  const hasChangedFlags = intent.orderedStops.some(stop => stop.isChanged === true)
  let body: BulkMoveStopsBody
  if (hasChangedFlags) {
    const rows = intent.orderedStops
      .filter(stop => isDailyStop(stop) || isOnRequestStop(stop))
      .map(stop => ({ id: String(stop.workOrderId), isChanged: stop.isChanged === true }))
    const batches = buildBulkResequenceBatch({ rows, destinationRouteId, serviceDate, updateMasterSequence })
    if (batches.length === 0) {
      // No surviving changed rows (the dialog disables save when nothing
      // is flagged — this is the defensive zero-changes branch). Exit clean
      // with preflight only; do NOT fall back to a full-reorder that would
      // rewrite positions the user didn't ask to touch.
      return {
        strategy: STRATEGY_ID,
        intent,
        steps: [makePreflightStep({ intent }, deps) as PipelineStep],
      }
    }
    body = {
      moves: batches.map(batch => ({
        destinationRouteId: batch.destinationRouteId,
        stops: [...batch.stops],
        serviceDate: batch.serviceDate,
        targetStopId: batch.targetStopId,
        updateMasterSequence: batch.updateMasterSequence,
      })),
    }
  } else {
    body = fullReorderBody
  }
  return {
    strategy: STRATEGY_ID,
    intent,
    steps: [
      makePreflightStep({ intent }, deps) as PipelineStep,
      makeBulkMutateStep({ body }, deps) as PipelineStep,
      makeVerifyStep({ intent, expectedFinalCount: workOrderIds.length }, deps) as PipelineStep,
    ],
  }
}

/** Mirrors `v3Slot.addressedRows` — every row this strategy has to name in a bulk payload. */
const addressedRows = (intent: RouteOpIntent): ReadonlyArray<Stop> => {
  switch (intent.kind) {
    case 'assign':
      return [...intent.stops, ...(intent.allOrderedStops ?? [])]
    case 'change-date':
    case 'unassign':
      return intent.stops
    case 'move':
      return [...intent.stops, ...(intent.destinationOrderedStops ?? [])]
    case 'resequence':
      return intent.orderedStops
    // Master-keyed intents carry no work order by design, so there is nothing here to address.
    default:
      return []
  }
}

// `DailyStop.workOrderId` is branded non-null, but a routable row reaches here with it unset — the
// board's `toIntentStop` copies the raw DTO field, which is null on a DISPOSAL row. Unguarded,
// `String(...)` turns that into the literal "undefined" and sends it as a stop id or a `targetStopId`,
// which Jackson rejects as a UUID and the user sees as an unretryable "please try again".
const countRowsMissingWorkOrderId = (intent: RouteOpIntent): number =>
  addressedRows(intent).filter(row => (isDailyStop(row) || isOnRequestStop(row)) && !row.workOrderId).length

// Whole-plan, mirroring `v3Slot.isFullyAddressable`: dropping the rows instead would send a partial
// order that `bulkMoveStops` re-keys, silently reshuffling the route.
const makeUnaddressableStep = (rowCount: number): PipelineStep => ({
  id: 'v1-bulk:unaddressable-rows',
  phase: 'PREFLIGHT',
  label: 'Failed to address every stop',
  applyOrder: 0,
  run: async () =>
    err({
      kind: 'PRECONDITION_FAILED',
      reason: `This route has ${rowCount} stop${rowCount === 1 ? '' : 's'} with no work order, which this route generation cannot address.`,
    }),
})

export const v1BulkStrategy: RouteOpStrategy = {
  id: STRATEGY_ID,
  plan(intent: RouteOpIntent, deps: Deps): OperationPlan {
    const unaddressable = countRowsMissingWorkOrderId(intent)
    if (unaddressable > 0) {
      return { strategy: STRATEGY_ID, intent, steps: [makeUnaddressableStep(unaddressable)] }
    }

    switch (intent.kind) {
      case 'unassign':
        return unassignPlan(intent, deps)
      case 'assign':
        return assignBulkPlan(intent, deps)
      case 'move':
        return moveBulkPlan(intent, deps)
      case 'resequence':
        return resequenceBulkPlan(intent, deps)
      case 'complete':
        // Completion has a single BE endpoint (bulk-complete) — no separate v1
        // bulk shape to mirror. Reuse the shared pipeline with the v1-bulk label.
        return { ...buildCompletePipeline(intent, deps), strategy: STRATEGY_ID }
      case 'change-date':
        return changeDateBulkPlan(intent, deps)
      case 'change-day-of-week':
        // Master-only — never went through any v1 bulk endpoint. Reuse the
        // v2 pipeline plan.
        return { ...buildChangeDayOfWeekPipeline(intent, deps), strategy: STRATEGY_ID }
      case 'master-publish':
        // No v1 bulk equivalent — `bulkMoveStops` collapses assign+resequence
        // into one batch, but master-publish needs separate UNASSIGN/ASSIGN/
        // RESEQUENCE phases (and a dayOfWeek master leg). Reuse v2 plan.
        return { ...buildMasterPublishPipeline(intent, deps), strategy: STRATEGY_ID }
      case 'restore-master-version':
        // Single atomic BE call — no v1 bulk equivalent. Reuse the v2 plan
        // with the v1-bulk strategy label for telemetry consistency.
        return { ...buildRestoreMasterVersionPipeline(intent, deps), strategy: STRATEGY_ID }
      case 'restore-daily-version':
        return { ...buildRestoreDailyVersionPipeline(intent, deps), strategy: STRATEGY_ID }
      case 'sync-daily-to-master':
        return { ...buildSyncDailyToMasterPipeline(intent, deps), strategy: STRATEGY_ID }
      default:
        return assertNever(intent)
    }
  },
}
